import _, { isEmpty } from "lodash";
import fp from "lodash/fp";
import moment from "moment";

import { USER_ROLES } from "helpers";
import { float6Normalizer } from "validators";

export class MiscFee {
	type = "0";
}

export class AdditionalFees {
	monthAnnualFeeBilled = 12;
	miscFees = [new MiscFee()];
	monthPCIAnnualFeeCharged = "12";
	discountType = "0";
	isMonthEnd = "true";
}

export const feesHardcode = {
	tinInvalidFee: "30.00",
	achRejectFee: "25.000000",
	merchantMonitoringFee: "49.950000",
	avs: "0.02",
	aru: "0.50",
	voiceAuth: "1.50",
	voiceAVS: "5.50",
	chargebackFee: "25.000000",
	gatewayPerAuth: "0.05",
	retrievalFee: "5.000000",
	pciNonCompilanceValues: [
		"0.00",
		"19.95",
		"30.00",
		"33.95",
		"50.00",
		"75.00",
	],
	annualFee: "119.000000",
	pciComplianceFee: "9.950000",
};

export const templateDiff = (template, application) => {
	if (!template || !application) return {};
	return _.reduce(
		template,
		(result, value, key) => {
			return application.hasOwnProperty(key) &&
				_.isEqual(value, application[key])
				? result
				: { ...result, [key]: value };
		},
		[],
	);
};

export const findFieldFromTemplate = (template, { fieldName, blockName }) => {
	if (fp.isEmpty(template)) return {};

	const block = template["fieldBlocks"].find(b => b.name === blockName);
	if (!block) return {};

	const field = block["fields"].find(f => f.name === fieldName);
	return field || {};
};

export const getProcessorId = application => application?.processor?.id;
export const getSponsorId = application => application?.sponsor?.id;

export const checkIsFiservNorthProcessor = application => {
	const processorId = getProcessorId(application);
	return ["5"].includes(processorId?.toString());
};

export const checkIsFiservFspProcessor = application => {
	const processorId = getProcessorId(application);
	return ["1"].includes(processorId?.toString());
};

export const checkIsTsysProcessor = application => {
	const processorId = getProcessorId(application);
	return ["2"].includes(processorId?.toString());
};

export const fromAppEntry = application =>
	fp.isEmpty(application?.applicationStatus);

export const getPrimaryAgentId = application => application?.primaryAgent?.id;

/**
 * Converts template to plain object.
 */
export const parseFeesTemplateInitialValues = template => {
	if (!template) return;

	const parseValue = (value, name) => {
		if (name === "isMonthEnd") {
			return value;
		} else if (name === "toBeBilled") {
			return value === "annually" ? "1" : "0";
		} else if (value === "true") {
			return true;
		} else if (value === "false") {
			return false;
		} else if (
			// prettier-ignore
			name === "passCardBrandFees" ||
			name === "FlatRate" ||
			name === "ERR" ||
			name === "InterchangePlus" ||
			name === "ThreeTier" ||
			name === "feeProgram" ||
			name === "amexType" ||
			name === "pinDebitSponsorship" ||
			name === "ebtAccountNumber" ||
			// MPA upload uses multipart/form-data
			name?.toLowerCase()?.includes("toBeBilled".toLowerCase()) ||
			name?.toLowerCase()?.includes("discountType".toLowerCase()) ||
			name?.toLowerCase()?.includes("monthAnnualFeeBilled".toLowerCase()) ||
			name?.toLowerCase()?.includes("monthPCIAnnualFeeCharged".toLowerCase()) ||
			name === "miscFees[0].Month" ||
			name === "miscFees[0].Percentage" ||
			name === "miscFees[0].Value" ||
			name === "miscFees[0].Type" ||	
			name === "miscFees[0].Description" ||
			name === "miscFees[1].Month" ||
			name === "miscFees[1].Percentage" ||
			name === "miscFees[1].Value" ||
			name === "miscFees[1].Type" ||
			name === "miscFees[1].Description" ||
			name === "miscFees[2].Month" ||
			name === "miscFees[2].Percentage" ||
			name === "miscFees[2].Value" ||
			name === "miscFees[2].Type" ||
			name === "miscFees[2].Description"
		) {
			return value;
		} else if (
			name === "miscFees[0].StartDate" ||
			name === "miscFees[0].EndDate" ||
			name === "miscFees[1].StartDate" ||
			name === "miscFees[1].EndDate" ||
			name === "miscFees[2].StartDate" ||
			name === "miscFees[2].EndDate"
		) {
			return moment(value).format("MM/DD/YYYY");
		} else {
			return float6Normalizer(value);
		}
	};

	const reduceField = (acc, field) => {
		if (field.value === null || field.value === "") return acc;

		return {
			...acc,
			[field.name]: parseValue(field.value, field.name),
		};
	};

	const reduceFieldBlock = fp.flow(
		fp.get("fields"),
		fp.reduce(reduceField, {}),
	);

	const allFeesTemplates = fp.flow(
		fp.get("fieldBlocks"),
		fp.map(reduceFieldBlock),
		fp.reduce(fp.merge, {}),
	)(template);

	const miscFeeTemplate = () => {
		const mistFee1: { [key: string]: any } = {};
		const mistFee2: { [key: string]: any } = {};

		for (let property in allFeesTemplates) {
			if (property.slice(0, 11) === "miscFees[0]") {
				const name = property.slice(12);
				const identityName = name[0].toLowerCase() + name.slice(1);
				mistFee1[identityName] = allFeesTemplates[property];
			}

			if (property.slice(0, 11) === "miscFees[1]") {
				const name = property.slice(12);
				const identityName = name[0].toLowerCase() + name.slice(1);
				mistFee2[identityName] = allFeesTemplates[property];
			}
		}

		let miscFees = [mistFee1, mistFee2].filter(
			el => Object.keys(el).length,
		);

		return isEmpty(miscFees) ? [{}] : miscFees;
	};

	const unicFeesTemplates = _.omit(allFeesTemplates, [
		"miscFees[0].Month",
		"miscFees[0].Percentage",
		"miscFees[0].Value",
		"miscFees[0].Type",
		"miscFees[0].StartDate",
		"miscFees[0].EndDate",
		"miscFees[0].Description",
		"miscFees[1].Month",
		"miscFees[1].Percentage",
		"miscFees[1].Value",
		"miscFees[1].Type",
		"miscFees[1].StartDate",
		"miscFees[1].EndDate",
		"miscFees[1].Description",
	]);

	const result = { ...unicFeesTemplates, miscFees: miscFeeTemplate() };

	return result;
};

export const removeFirstUpperLetterFromTemplate = template => {
	const fieldBlocks = template?.fieldBlocks?.map(fieldBlocks => {
		const fields = fieldBlocks.fields?.map(fields => {
			const name = fields.name.split(".")?.reduce((prevStr, str) => {
				str = str[0].toLowerCase() + str.slice(1);
				return prevStr + "." + str;
			});
			return { ...fields, name };
		});
		return { ...fieldBlocks, fields };
	});
	return { ...template, fieldBlocks };
};

export const applyHardCodeValues = (
	initialValues,
	feesHardcode,
	application,
	isEditMode,
) => {
	const isFiserv = checkIsFiservFspProcessor(application);
	const isTsys = checkIsTsysProcessor(application);

	initialValues.chargebackFee = feesHardcode.chargebackFee;
	initialValues.achRejectFee = feesHardcode.achRejectFee;
	initialValues.merchantMonitoringFee = feesHardcode.merchantMonitoringFee;
	initialValues.retrievalFee = feesHardcode.retrievalFee;

	if (application?.primaryAgent?.role?.name === USER_ROLES.InsideSales) {
		initialValues.annualFee = feesHardcode.annualFee;
		initialValues.pciComplianceFee = feesHardcode.pciComplianceFee;
	}
	if (isTsys && !isEditMode) {
		initialValues.tinInvalidFee = feesHardcode.tinInvalidFee;
	} else if (isFiserv) {
		initialValues.tinInvalidFee = feesHardcode.tinInvalidFee;
		initialValues.avs = feesHardcode.avs;
		initialValues.aru = feesHardcode.aru;
		initialValues.voiceAuth = feesHardcode.voiceAuth;
		initialValues.voiceAVS = feesHardcode.voiceAVS;
		initialValues.gatewayPerAuth = feesHardcode.gatewayPerAuth;
		if (
			!feesHardcode.pciNonCompilanceValues.find(
				v => Number(v) === Number(initialValues.pciNonCompilance),
			)
		) {
			initialValues.pciNonCompilance = "";
		}
	}

	return initialValues;
};

/**
 * Merges Monthly fees template to other template.
 */
export const mergeMonthlyTemplate = ({ template, monthlyTemplate }) => {
	if (!template) {
		return monthlyTemplate;
	}
	if (!monthlyTemplate) {
		return template;
	}

	const findMonthlyFeesFieldBlock = fp.find(["name", "MonthlyFees"]);

	// prettier-ignore
	const monthlyFeesFieldBlock: any =
		findMonthlyFeesFieldBlock(monthlyTemplate?.fieldBlocks);

	let fieldBlocks: any = [];
	if (template?.fieldBlocks) {
		fieldBlocks = fieldBlocks.concat(template.fieldBlocks);
	}

	if (monthlyFeesFieldBlock) {
		fieldBlocks.push(monthlyFeesFieldBlock);
	}

	const customName = template?.name
		? `${template?.name} + Monthly`
		: "Monthly";

	const mergedTemplate = {
		...template,
		name: customName,
		fieldBlocks: fieldBlocks,
	};
	return mergedTemplate;
};
